import { 
  Image, 
  Video,
  MessageSquare, 
  Users, 
  Download, 
  Heart,
  Shield,
  Share2
} from "lucide-react";
import { FeatureHighlight } from "./FeatureHighlight";
import photosToLifeDemo from "@/assets/photos-to-life-demo.mp4";
import obituaryDemo from "@/assets/obituary-demo.mp4";

const featuredValues = [
  {
    icon: Image,
    title: "Bring Photos to Life",
    description: "Transform cherished still images into meaningful visual stories. Our AI adds subtle motion, warmth, and life to your favorite photographs, creating an emotional viewing experience that captures the essence of your loved one.",
    imageSrc: photosToLifeDemo,
    imageAlt: "Demonstration of photos being transformed with subtle animation effects",
    linkTo: "/photos-to-life",
    linkText: "Explore Photo Animation",
    imagePosition: "left" as const,
    isVideo: true,
  },
  {
    icon: Video,
    title: "Publish a Tribute Video",
    description: "Create a beautiful, heartfelt tribute video that weaves together photos and music into a flowing narrative. Share it at memorial services, with distant family, or keep it as a lasting keepsake for generations to come.",
    imageSrc: "https://images.unsplash.com/photo-1485846234645-a62644f84728?w=600&h=400&fit=crop&q=80",
    imageAlt: "Preview of a tribute video being created with photos and music",
    linkTo: "/tribute-video",
    linkText: "Create a Tribute Video",
    imagePosition: "right" as const,
  },
  {
    icon: MessageSquare,
    title: "Craft a Beautiful Obituary",
    description: "Find the right words with our supportive writing tools and AI assistant. Whether you need gentle guidance or a starting point, we help you express love, gratitude, and remembrance in a way that truly honors their story.",
    imageSrc: obituaryDemo,
    imageAlt: "Demonstration of the obituary writing assistant with helpful prompts",
    linkTo: "/craft-obituary",
    linkText: "Start Writing an Obituary",
    imagePosition: "left" as const,
    isVideo: true,
  },
  {
    icon: Users,
    title: "Collaborate With Family & Friends",
    description: "Invite loved ones to contribute their own stories, photos, and cherished memories. Together, you create a richer, more complete celebration of a life well lived, bringing everyone closer even from afar.",
    imageSrc: "https://images.unsplash.com/photo-1529156069898-49953e39b3ac?w=600&h=400&fit=crop&q=80",
    imageAlt: "Multiple contributors adding photos and stories to a shared tribute",
    linkTo: "/collaborate",
    linkText: "See How Collaboration Works",
    imagePosition: "right" as const,
  },
];

const compactValues = [
  {
    icon: Download,
    title: "Download Everything to Keep Forever",
    description: "Securely save all written content, photos, and videos for personal remembrance or future generations.",
  },
  {
    icon: Heart,
    title: "Support Those Who Need It Most",
    description: "Easily collect donations to help family members or contribute to meaningful charities in honor of a loved one.",
  },
  {
    icon: Shield,
    title: "Private & Secure by Default",
    description: "Control who sees your tribute with password protection, private links, and granular sharing permissions.",
  },
  {
    icon: Share2,
    title: "Share With Everyone Who Matters",
    description: "Send via text, email, or social media. Print QR codes for services. Reach loved ones near and far with one click.",
  },
];

export const ValueProposition = () => {
  return (
    <div className="bg-background">
      {/* Section Header */}
      <section className="py-16 md:py-24 bg-background">
        <div className="container mx-auto px-4">
          <div className="text-center max-w-3xl mx-auto">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-4">
              Everything You Need to Honor Their Story
            </h2>
            <p className="text-lg text-muted-foreground">
              Collect photos and memories together, write obituaries and eulogies with guided assistance, animate cherished photos, and create beautiful tribute videos and memorial pages.
            </p>
          </div>
        </div>
      </section>

      {/* Featured Alternating Sections */}
      {featuredValues.map((feature, index) => (
        <FeatureHighlight
          key={feature.title}
          title={feature.title}
          description={feature.description}
          imageSrc={feature.imageSrc}
          imageAlt={feature.imageAlt}
          linkTo={feature.linkTo}
          linkText={feature.linkText}
          imagePosition={feature.imagePosition}
          index={index}
          icon={feature.icon}
          isVideo={'isVideo' in feature ? feature.isVideo : false}
        />
      ))}

      {/* Compact Cards for Remaining Features */}
      <section className="py-16 md:py-24 bg-background">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8 max-w-6xl mx-auto">
            {compactValues.map((value, index) => {
              const Icon = value.icon;
              return (
                <div
                  key={value.title}
                  className="group relative bg-card rounded-2xl p-6 lg:p-8 border border-border/50 hover:border-accent/50 hover:shadow-lg transition-all duration-300"
                  style={{ 
                    animationDelay: `${index * 0.1}s`,
                    animation: 'fadeInUp 0.6s ease-out forwards',
                    opacity: 0
                  }}
                >
                  <div className="w-12 h-12 rounded-xl bg-accent/10 flex items-center justify-center mb-5 group-hover:bg-accent/20 transition-colors">
                    <Icon className="h-6 w-6 text-accent" />
                  </div>
                  <h3 className="text-lg font-semibold text-foreground mb-2">
                    {value.title}
                  </h3>
                  <p className="text-muted-foreground text-sm leading-relaxed">
                    {value.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>
      
      <style>{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </div>
  );
};
